import { useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import { alertDialog } from "../utils/dialog";
import { copyText } from "../utils/copyText";
import { useInstallOption, startInstall, isStandalone } from "../utils/installPrompt";

// The page an invite points at: nothing but the app's name and one big
// Install button. Android Chrome only offers a real install dialog once it
// decides the site is installable (a moment after loading), so the manual
// steps sit underneath as a fallback for when that button isn't there.
export default function Install() {
  const option = useInstallOption();
  const [busy, setBusy] = useState(false);
  const [installed, setInstalled] = useState(false);
  const alreadyInstalled = isStandalone();

  // Instagram/Facebook/etc. open links in their own mini-browser, which can't
  // install anything — the person has to open the same link in Chrome.
  const ua = navigator.userAgent;
  const isAndroid = /Android/i.test(ua);
  const inAppBrowser = /FBAN|FBAV|Instagram|Line\/|; wv\)/i.test(ua);
  // Only Chrome (and Samsung Internet / Edge, which also hand the page an
  // install button) offer a one-tap install. Everything else — Firefox, Opera,
  // the mini-browsers inside other apps — has to be sent over to Chrome.
  // Samsung Internet and Edge carry "Chrome/" in their user agent too, so they
  // are told apart here only to word the fallback.
  const isRealChrome =
    /Chrome\//.test(ua) &&
    !/SamsungBrowser|EdgA|OPR\/|OPT\/|Firefox|Vivaldi|DuckDuckGo|YaBrowser|UCBrowser/i.test(ua) &&
    !inAppBrowser;

  // Hands this page to Chrome from any other Android browser. If Chrome isn't
  // installed the browser falls back to opening the plain link.
  function openInChrome() {
    const target = "https://gatheryou.in/install";
    window.location.href =
      "intent://gatheryou.in/install#Intent;scheme=https;package=com.android.chrome;" +
      `S.browser_fallback_url=${encodeURIComponent(target)};end`;
  }

  // iPhones can't be sent to Safari from another app or browser (Apple offers
  // nothing like Android's intent link), so outside Safari the person copies
  // the link and opens it there themselves. Chrome/Firefox/Edge on iOS carry
  // their own token in the user agent; an app's mini-browser has no "Safari/"
  // token at all.
  const isRealSafari = /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua);
  const needsSafari = option === "ios" && !isRealSafari;
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    if (await copyText("https://gatheryou.in/install")) {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } else {
      alertDialog({
        title: "Copy this link",
        message: "Open Safari and go to gatheryou.in/install"
      });
    }
  }

  async function handleInstall() {
    if (busy) return;
    setBusy(true);
    const outcome = await startInstall(option);
    setBusy(false);
    if (outcome === "accepted") setInstalled(true);
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <BrandLogo size={56} />
        </div>

        <h1 style={title}>Get GatherYou on your phone</h1>
        <p style={subtitle}>
          Discover and share embroidery designs. It opens like any other app, right from your home screen.
        </p>

        {alreadyInstalled || installed ? (
          <>
            <p style={okText}>
              {installed
                ? "Installed! You can close this tab now and open GatherYou from your home screen."
                : "You're already using the GatherYou app."}
            </p>
            <Link to="/" style={secondaryLink}>
              Continue to GatherYou
            </Link>
          </>
        ) : (
          <>
            {option && !needsSafari && (
              <button style={{ ...installBtn, opacity: busy ? 0.7 : 1 }} onClick={handleInstall} disabled={busy}>
                Install GatherYou
              </button>
            )}

            {needsSafari && (
              <button style={installBtn} onClick={copyLink}>
                {copied ? "Link copied ✓" : "Copy link"}
              </button>
            )}

            {!option && isAndroid && !isRealChrome && (
              <button style={installBtn} onClick={openInChrome}>
                Open in Chrome
              </button>
            )}

            {(!option || option === "ios") && (
              <div style={steps}>
                {needsSafari ? (
                  <>
                    <p style={stepsHeading}>Install from Safari</p>
                    <ol style={list}>
                      <li>Tap “Copy link” above</li>
                      <li>Open Safari and paste it in the address bar</li>
                      <li>In Safari, tap Share (or ⋯ then Share), then “Add to Home Screen”</li>
                    </ol>
                    <p style={{ ...list, paddingLeft: 0, marginTop: 8 }}>
                      In Instagram or WhatsApp you can also tap the ⋯ or Share icon and choose “Open in Safari”.
                    </p>
                  </>
                ) : option === "ios" ? (
                  <>
                    <p style={stepsHeading}>On iPhone or iPad</p>
                    <ol style={list}>
                      <li>Open this page in Safari</li>
                      <li>Tap Share (on newer iPhones it is inside the ⋯ menu)</li>
                      <li>Choose “Add to Home Screen”</li>
                    </ol>
                  </>
                ) : !isAndroid ? (
                  <p style={{ ...list, paddingLeft: 0 }}>
                    Open this link on your phone (Chrome on Android, or Safari on iPhone) to install GatherYou.
                  </p>
                ) : isRealChrome ? (
                  <>
                    <p style={stepsHeading}>On Android</p>
                    <ol style={list}>
                      <li>Tap the ⋮ menu (top right)</li>
                      <li>Choose “Install app”</li>
                    </ol>
                  </>
                ) : (
                  <>
                    <p style={stepsHeading}>
                      {inAppBrowser ? "This mini-browser can't install apps" : "Works best in Chrome"}
                    </p>
                    <ol style={list}>
                      <li>Tap “Open in Chrome” above</li>
                      <li>In Chrome, tap ⋮ and choose “Install app”</li>
                    </ol>
                    <p style={{ ...list, paddingLeft: 0, marginTop: 8 }}>
                      Or look in this browser’s own menu for “Install app” or “Add to Home screen”.
                    </p>
                  </>
                )}
              </div>
            )}

            <Link to="/" style={secondaryLink}>
              Skip, use it in the browser
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

const page = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 20px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)"
};

const card = {
  width: "100%",
  maxWidth: 380,
  textAlign: "center",
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 18,
  padding: "28px 22px",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)"
};

const title = {
  fontSize: 22,
  fontWeight: 800,
  color: "var(--text-heading)",
  margin: "0 0 8px"
};

const subtitle = {
  fontSize: 14,
  lineHeight: 1.5,
  color: "var(--text-body)",
  margin: "0 0 22px"
};

const installBtn = {
  width: "100%",
  padding: "14px",
  borderRadius: 12,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer"
};

const steps = {
  textAlign: "left",
  background: "var(--bg-soft)",
  borderRadius: 12,
  padding: "12px 16px",
  marginTop: 14
};

const stepsHeading = {
  fontSize: 13,
  fontWeight: 700,
  color: "var(--text-heading)",
  margin: "0 0 6px"
};

const list = {
  margin: 0,
  paddingLeft: 20,
  fontSize: 13.5,
  lineHeight: 1.6,
  color: "var(--text-secondary)"
};

const okText = {
  fontSize: 14,
  fontWeight: 600,
  color: "var(--accent-success)",
  margin: "0 0 14px"
};

const secondaryLink = {
  display: "inline-block",
  marginTop: 16,
  fontSize: 13.5,
  fontWeight: 600,
  color: "var(--text-muted)",
  textDecoration: "none"
};
